<template>
    <v-container :fluid="true" class="max-height">
        <v-row>
            <v-col class="px-5" cols="12" sm="6">
                <h1 class="text-left">Message Logs</h1>
            </v-col>
            <v-col cols="12" sm="6">
                <v-row>
                    <v-col class="pb-1" cols="6">
<v-autocomplete
                            v-model="formData['device_id']"
                            v-model:search="deviceSearch"
                            :items="deviceItems"
                            :loading="devicesLoading"
                            density="compact"
                            item-title="title"
                            item-value="id"
                            label="Device"
                            placeholder="Type a serial or asset name"
                            no-data-text="No matching devices"
                            hide-details
                            clearable
                            multiple
                            no-filter
                            @update:model-value="fetchMessageLogs(1)"/>
                        <!-- Older links still arrive with ?asset_id=. The asset
                             picker is gone, so say so rather than filter
                             invisibly, and give a way to clear it. -->
                        <v-chip v-if="formData['asset_id'] && formData['asset_id'].length"
                                class="mt-2" closable color="primary" size="small" variant="tonal"
                                @click:close="clearAssetFilter">
                            Filtered to a linked asset
                        </v-chip>
                    </v-col>
                    <v-col class="pb-1" cols="6">
                        <v-select
                            v-model="formData['packet_type']"
                            :items="packetTypes"
                            label="Message Type"
                            density="compact"
                            hide-details
                            clearable
                            multiple
                            @update:model-value="fetchMessageLogs(1)"/>
                    </v-col>

                    <v-col class="pb-1" cols="6">
                        <v-select
                            v-model="formData['trigger_source']"
                            :items="packetTriggerSource"
                            density="compact"
                            hide-details
                            label="Trigger Source"
                            clearable
                            multiple
                            @update:model-value="fetchMessageLogs(1)"/>
                    </v-col>
                    <v-col class="pb-1" cols="6">
                        <v-text-field
                            v-model="formData['packet_id']"
                            clearable
                            density="compact"
                            hide-details
                            label="Packet ID"
                            type="numeric"
                            @update:model-value="fetchMessageLogs(1)"/>
                    </v-col>

                    <v-col cols="6">
                        <v-date-input
                            v-model="formData['from_date']"
                            append-inner-icon="$calendar"
                            density="compact"
                            hide-actions
                            label="From Date"
                            prepend-icon=""
                            clearable
                            @click:clear="()=>{formData['from_date']=null;fetchMessageLogs(1)}"
                            @update:model-value="fetchMessageLogs(1)"/>
                    </v-col>
                    <v-col cols="6">
                        <v-date-input
                            v-model="formData['to_date']"
                            append-inner-icon="$calendar"
                            density="compact"
                            label="To Date"
                            prepend-icon=""
                            hide-actions
                            clearable
                            @click:clear="()=>{formData['to_date']=null;fetchMessageLogs(1)}"
                            @update:model-value="fetchMessageLogs(1)"/>
                    </v-col>
                </v-row>
                <div class="mt-5 float-end">
                    <v-btn v-if="user.type === 'admin'" class="me-3" icon="mdi-download" size="small" variant="tonal"
                           @click="exportLogs"/>
                    <v-btn icon="mdi-refresh" size="small" variant="tonal" @click="refresh"/>
                </div>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-table>
                    <thead>
                    <tr>
                        <th></th>
                        <th>Asset</th>
                        <th-btn :sortedColumn="sortedColumn??null" column="raw_packet_id"
                                sortable title="Packet ID" @sort="sort"/>
                        <th-btn :sortedColumn="sortedColumn??null" column="direction"
                                sortable title="Direction" @sort="sort"/>
                        <th-btn :sortedColumn="sortedColumn??null" column="packet_type"
                                sortable title="Message Type" @sort="sort"/>
                        <th-btn :sortedColumn="sortedColumn??null" column="trigger_source"
                                sortable title="Triggered by" @sort="sort"/>
                        <th-btn :sortedColumn="sortedColumn??null" column="accuracy"
                                sortable title="Accuracy" @sort="sort"/>
                        <th-btn :sortedColumn="sortedColumn??null" column="ttff"
                                sortable title="TTFF" @sort="sort"/>
                        <th-btn :sortedColumn="sortedColumn??null" column="odometer"
                                sortable title="Odometer" @sort="sort"/>
                        <th>Payload</th>
                        <th-btn :sortedColumn="sortedColumn??null" column="re_transmit"
                                sortable title="Re-transmit" @sort="sort"/>
                        <th-btn :sortedColumn="sortedColumn??null" column="event_time"
                                sortable title="Event Time" @sort="sort"/>
                        <th-btn :sortedColumn="sortedColumn??null" column="transmit_time"
                                sortable title="Origination Time" @sort="sort"/>
                        <th-btn :sortedColumn="sortedColumn??null" column="received_time"
                                sortable title="Received Time" @sort="sort"/>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(messageLog , index) in messageLogs" v-if="!loading && messageLogs.length"
                        :key="messageLog.id" class="text-left"
                        :style="{'background-color': getSeverityColor(messageLog.severity)}">
                        <td>
                            <v-icon size="small" v-bind="signals(messageLog.rsrp,messageLog.rsrq)"/>
                        </td>
                        <td>
                            <a v-if="messageLog.asset_id"
                               class="text-primary cursor-pointer text-decoration-none"
                               @click="navigateTo('asset', {asset: messageLog.asset_id})">{{ messageLog.asset_label || messageLog.slno }}</a>
                            <template v-else>{{ messageLog.slno }}</template>
                            <!-- Only when the label is what is shown above; otherwise
                                 the serial would be printed twice. -->
                            <div v-if="messageLog.asset_label && messageLog.slno"
                                 class="text-caption">{{ messageLog.slno }}</div>
                        </td>
                        <td>{{ messageLog.raw_packet_id }}</td>
                        <td>{{ messageLog.direction }}</td>
                        <td>
                            {{ strOperations().formatEnumString(messageLog.packet_type) }}
                            <v-tooltip v-if="messageLog.severity_label" :text="messageLog.severity_label" location="top">
                                <template #activator="{ props }">
                                    <v-icon v-bind="props" :icon="getSeverityIcon(messageLog.severity)" :color="getSeverityIconColor(messageLog.severity)" size="x-small" class="ms-1"/>
                                </template>
                            </v-tooltip>
                        </td>
                        <td>
                            <v-tooltip v-if="messageLog.verbose_text" :text="messageLog.verbose_text" location="top" max-width="400">
                                <template #activator="{ props }">
                                    <span v-bind="props" class="cursor-help">{{ messageLog.trigger_source && messageLog.trigger_source.toLowerCase() !== 'unknown' ? strOperations().formatEnumString(messageLog.trigger_source) : '' }}</span>
                                </template>
                            </v-tooltip>
                            <span v-else>{{ messageLog.trigger_source && messageLog.trigger_source.toLowerCase() !== 'unknown' ? strOperations().formatEnumString(messageLog.trigger_source) : '' }}</span>
                        </td>
                        <td>{{ messageLog.accuracy }}</td>
                        <td>{{ messageLog.ttff }}</td>
                        <td>{{ strOperations().unitifyDistance(messageLog.odometer, 'distance', 'km') }}</td>
                        <td class="cursor-pointer" @click="showPayload(messageLog)">
                            <v-icon icon="mdi-information"/>
                        </td>
                        <td :style="{'background-color': getMessageDuplicationHighlightColor(messageLog.re_transmit)}">
                            {{ messageLog.re_transmit }}
                        </td>
                        <td>{{ messageLog.event_time ? formatDateTime(messageLog.event_time) : '' }}
                        </td>
                        <td>
                            {{
                                messageLog.transmit_time ? formatDateTime(messageLog.transmit_time) : ''
                            }}
                        </td>
                        <td :style="{'background-color': getTimestampColor(messageLog.received_time,messageLog.transmit_time)}">
                            {{
                                messageLog.received_time ? formatDateTime(messageLog.received_time) : ''
                            }}
                        </td>
                    </tr>
                    <tr v-else>
                        <td v-if="loading" colspan="12">
                            <v-progress-circular color="primary" indeterminate/>
                        </td>
                        <td v-else colspan="12">No record found</td>
                    </tr>
                    </tbody>
                </v-table>
            </v-col>
        </v-row>

        <v-row v-if="totalPages > 1" justify="center">
            <v-col cols="8">
                <v-container class="max-width">
                    <v-pagination
                        v-model="currentPage"
                        :length="totalPages"
                        class="my-4"
                        density="compact"
                        @click="fetchMessageLogs"
                    ></v-pagination>
                </v-container>
            </v-col>
        </v-row>
    </v-container>

    <v-dialog v-model="dialogSwitch" max-width="1000" scrollable>
        <v-card>
            <v-card-title>
                <b>{{ strOperations().formatEnumString(selectedMessage.packet_type) }}</b> Message Payload
            </v-card-title>

            <v-card-text class="pa-4">
                <v-row no-gutters>
                    <v-col v-if="payloadGps" cols="4" class="pe-3">
                        <div style="height: 300px; border-radius: 8px; overflow: hidden; border: 1px solid #444;">
                            <l-map
                                ref="payloadMap"
                                :zoom="15"
                                :center="[payloadGps.latitude, payloadGps.longitude]"
                                :use-global-leaflet="false"
                                :options="{zoomControl: false, attributionControl: false}"
                            >
                                <l-tile-layer :url="$mapTiles.street"/>
                                <l-marker :lat-lng="[payloadGps.latitude, payloadGps.longitude]"/>
                            </l-map>
                        </div>
                        <div class="mt-2 text-caption text-medium-emphasis">
                            <div><b>Lat:</b> {{ payloadGps.latitude?.toFixed(6) }}</div>
                            <div><b>Lng:</b> {{ payloadGps.longitude?.toFixed(6) }}</div>
                            <div v-if="payloadGps.speed_kmh2 != null"><b>Speed:</b> {{ payloadGps.speed_kmh2 }} km/h</div>
                            <div v-if="payloadGps.heading_deg != null"><b>Heading:</b> {{ payloadGps.heading_deg }}°</div>
                            <div v-if="payloadGps.altitude != null"><b>Altitude:</b> {{ payloadGps.altitude }}m</div>
                            <div v-if="payloadGps.accuracy != null"><b>Accuracy:</b> {{ payloadGps.accuracy }}m</div>
                            <div v-if="payloadGps.satellite_count != null"><b>Satellites:</b> {{ payloadGps.satellite_count }}</div>
                            <v-btn size="small" variant="text" icon="mdi-google-maps" color="blue" class="mt-1"
                                   :href="`https://www.google.com/maps?q=${payloadGps.latitude},${payloadGps.longitude}`"
                                   target="_blank" title="Open in Google Maps"/>
                        </div>
                    </v-col>
                    <v-col :cols="payloadGps ? 8 : 12">
                        <div v-if="selectedMessage.obd_params" class="mb-3">
                            <div class="text-caption font-weight-bold mb-1">
                                J1939 Parameters
                                <span v-if="!selectedMessage.obd_params.status_ok" class="text-medium-emphasis">
                                    (no parameters reported)
                                </span>
                            </div>
                            <v-table density="compact">
                                <tbody>
                                <tr v-for="param in selectedMessage.obd_params.params" :key="param.key">
                                    <td>{{ param.label }}</td>
                                    <td class="text-right" :class="{'text-medium-emphasis': param.display === 'Not reported'}">
                                        {{ param.display }}
                                    </td>
                                </tr>
                                </tbody>
                            </v-table>
                            <v-divider class="mt-2"/>
                        </div>
                        <pre style="max-height: 500px; overflow-y: auto;">{{ selectedMessage.message_payload }}</pre>
                        <v-btn size="small" variant="text" icon="mdi-content-copy" color="blue-grey" class="mt-1"
                               @click="copyToClipboard(JSON.stringify(selectedMessage.message_payload, null, 2))"
                               title="Copy Payload"/>
                        <div v-if="selectedMessage.raw_hex" class="mt-3">
                            <v-divider class="mb-2"/>
                            <div class="d-flex align-center mb-1">
                                <span class="text-caption font-weight-bold">Raw Decrypted Hex</span>
                                <v-btn size="small" variant="text" icon="mdi-content-copy" color="blue-grey" class="ms-1"
                                       @click="copyToClipboard(selectedMessage.raw_hex)" title="Copy Hex"/>
                            </div>
                            <pre class="pa-2 bg-grey-darken-4 text-green-lighten-2 rounded" style="max-height: 200px; overflow-y: auto; word-break: break-all; white-space: pre-wrap; font-size: 12px;">{{ selectedMessage.raw_hex }}</pre>
                        </div>
                    </v-col>
                </v-row>
            </v-card-text>

            <v-card-actions></v-card-actions>
        </v-card>
    </v-dialog>

    <Export-Logs v-if="user.type === 'admin' && exportLogsDialogSwitch" v-model="exportLogsDialogSwitch"
                 :assets="assets"/>
</template>

<script setup>
import {computed, onMounted, onUnmounted, reactive, ref, watch} from "vue";
import {useRoute} from "vue-router";
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import {VDateInput} from 'vuetify/labs/VDateInput'
import {strOperations, useNavigation} from "@/composables/globals";
import ThBtn from "@/components/globals/th-btn.vue";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {formatDateTime} from '@/composables/useTimezone';
import ExportLogs from "@/components/dialogs/devices/ExportLogs.vue";
import {LMap, LTileLayer, LMarker} from "@vue-leaflet/vue-leaflet";
import {useClipboard} from "@/composables/clipboard";

const route = useRoute();
const {navigateTo} = useNavigation();

const {copyToClipboard} = useClipboard();

const assets = ref([]);
const devices = ref([]);
const selectedDevices = ref([]);
const deviceSearch = ref('');
const devicesLoading = ref(false);
let deviceSearchTimer = null;
let deviceFetchSeq = 0;

// Union of the current results and whatever is selected. Vuetify renders a
// model value that is missing from items as the bare id, and it resets the
// search on selection - which refetches and would drop the item just picked.
const deviceItems = computed(() => {
    const seen = new Set(devices.value.map((d) => d.id));
    return [...devices.value, ...selectedDevices.value.filter((d) => !seen.has(d.id))];
});
const {user} = useAuthStore();
const loading = ref(true);
const totalPages = ref(1);
const currentPage = ref(1);
const messageLogs = ref([]);
const sortedType = ref(null);
const sortedColumn = ref(null);
const dialogSwitch = ref(false);
const selectedMessage = ref(null);
const {showSnackbar} = useSnackbarStore();
const exportLogsDialogSwitch = ref(false);

const payloadGps = computed(() => {
    const gps = selectedMessage.value?.message_payload?.gps_header;
    if (gps && gps.latitude && gps.longitude && gps.latitude !== 0 && gps.longitude !== 0) {
        return gps;
    }
    return null;
});

// Accept deep-link query params so screens like Device Details can jump
// here focused on a specific device or asset. `asset_id` is a
// multi-select so it can take a single number, a repeated query param,
// or a comma-separated list. `device_id` is single-valued.
onMounted(() => {
    const rawAsset = route.query.asset_id;
    if (rawAsset != null) {
        const toIds = (v) => String(v).split(',').map((s) => Number(s.trim())).filter((n) => Number.isFinite(n));
        formData.asset_id = Array.isArray(rawAsset)
            ? rawAsset.flatMap(toIds)
            : toIds(rawAsset);
    }
    const rawDevice = route.query.device_id;
    if (rawDevice != null) {
        // An array: the picker is multi-select and the API takes either shape.
        const toIds = (v) => String(v).split(',').map((s) => Number(s.trim())).filter((n) => Number.isFinite(n));
        formData.device_id = Array.isArray(rawDevice) ? rawDevice.flatMap(toIds) : toIds(rawDevice);
    }
    // Resolve any deep-linked ids to their serials so the picker shows a name
    // rather than a number; otherwise just prime the first page.
    fetchDevices('', formData.device_id);
    fetchMessageLogs();
});

const packetTypes = [
    {value: 0, title: "ACK"},
    {value: 1, title: "Location"},
    {value: 2, title: "Debug"},
    {value: 3, title: "OTA Download Request"},
    {value: 4, title: "OTA Ack"},
    {value: 5, title: "OTA Status"},
    {value: 6, title: "BootUp"},
    {value: 7, title: "Push Button"},
    {value: 9, title: "Geo Trigger Alert"},
    {value: 10, title: "WiFi Scan Result"},
    {value: 11, title: "Cell Tower Information"},
    {value: 12, title: "BLE Scan Result"},
    {value: 13, title: "Light Sensor Alert"},
    {value: 14, title: "Temp Sensor Alert"},
    {value: 15, title: "Config Download Request"},
    {value: 16, title: "Config Download Status"},
    {value: 17, title: "Diagnostic Data"},
    {value: 19, title: "Speed Alert"},
    {value: 20, title: "Ble Beacon Alert"},
    {value: 21, title: "LTE Scan Request"},
    {value: 22, title: "LTE Scan Response"},
    {value: 23, title: "Ext GPIO"},
    {value: 24, title: "OBD Data Vin"},
    {value: 25, title: "OBD Data Mil"},
    {value: 32, title: "OBD Data Dtc"},
    {value: 34, title: "CARB Capture"},
    {value: 35, title: "OBD Data Params"},
    {value: 36, title: "OBD Data Dtc J1939"},
    {value: 37, title: "OBD Data Mil J1939"},
    {value: 255, title: "Nack"},
]
const packetTriggerSource = [
    {value: 0, title: "Unknown"},
    {value: 1, title: "Network Joined"},
    {value: 2, title: "Motion Started"},
    {value: 3, title: "Motion in Progress"},
    {value: 4, title: "Motion Ended"},
    {value: 5, title: "Charger Connected"},
    {value: 6, title: "Charger Disconnected"},
    {value: 7, title: "Periodic Heartbeat"},
    {value: 8, title: "Push Button Trigger"},
    {value: 9, title: "Command Line"},
    {value: 10, title: "Bluetooth Connection"},
    {value: 11, title: "Remote"},
    {value: 12, title: "Low Battery"},
    {value: 13, title: "Light Sensor Event"},
    {value: 14, title: "Battery Removed"},
    {value: 15, title: "Battery Connected"},
    {value: 16, title: "Live Track"},
    {value: 17, title: "Temp Alert"},
    {value: 18, title: "Geofence Alert"},
    {value: 19, title: "Speed Alert"},
    {value: 20, title: "Bluetooth Beacon Alert"},
    {value: 21, title: "Ext GPI"},
    {value: 22, title: "Vehicle Check"},
    {value: 23, title: "Crash Alert"},
]
const getTimestampColor = (receiveTimestamp, transmitTimestamp) => {
    const diffInSeconds = Math.abs(new Date(receiveTimestamp) - new Date(transmitTimestamp)) / 1000;

    if (diffInSeconds <= 60) {
        return 'transparent'; // No change
    } else if (diffInSeconds <= 120) {
        return '#FFF4E5'; // Very light orange
    } else if (diffInSeconds <= 300) {
        return '#FFE0B2'; // Light orange
    } else if (diffInSeconds <= 600) {
        return '#FFB74D'; // Orange
    } else {
        return '#FF3D00';
    }
}
const getSeverityColor = (severity) => {
    switch (severity) {
        case 'warning':    return '#FFF0F0';  // Light red
        case 'concerning': return '#FFCCCC';  // Medium red
        case 'critical':   return '#FF9999';  // Dark red
        default:           return 'transparent';
    }
}
const getSeverityIcon = (severity) => {
    switch (severity) {
        case 'warning':    return 'mdi-alert-outline';
        case 'concerning': return 'mdi-alert';
        case 'critical':   return 'mdi-alert-octagon';
        default:           return '';
    }
}
const getSeverityIconColor = (severity) => {
    switch (severity) {
        case 'warning':    return 'orange';
        case 'concerning': return 'deep-orange';
        case 'critical':   return 'red';
        default:           return 'grey';
    }
}
const getMessageDuplicationHighlightColor = (count) => {
    if (!count) {
        return 'transparent'; // No change
    } else if (count == 2) {
        return '#FFF4E5'; // Very light orange
    } else if (count == 3) {
        return '#FFE0B2'; // Light orange
    } else if (count == 4) {
        return '#FFB74D'; // Orange
    } else {
        return '#FF3D00';
    }
}
const calculateSignalBars = (rsrp, rsrq) => {
    // Clamp input values
    rsrp = Math.max(0, Math.min(rsrp, 97))
    rsrq = Math.max(0, Math.min(rsrq, 34))

    // Convert to physical units
    let rsrp_dbm = -140 + rsrp
    let rsrq_db = -19.5 + 0.5 * rsrq

    let rsrp_score = 1;
    // Score RSRP
    if (rsrp_dbm >= -80) {
        rsrp_score = 5
    } else if (rsrp_dbm >= -90) {
        rsrp_score = 4
    } else if (rsrp_dbm >= -100) {
        rsrp_score = 3
    } else if (rsrp_dbm >= -110) {
        rsrp_score = 2
    }

    let rsrq_score = 1
    // Score RSRQ
    if (rsrq_db >= -10) {
        rsrq_score = 5
    } else if (rsrq_db >= -13) {
        rsrq_score = 4
    } else if (rsrq_db >= -16) {
        rsrq_score = 3
    } else if (rsrq_db >= -19) {
        rsrq_score = 2
    }

    // Weighted average: slightly reduced RSRP weight
    let combined_score = 0.6 * rsrp_score + 0.4 * rsrq_score
    // Adjusted threshold mapping for more realistic bars
    if (combined_score >= 4.8) {
        return 5
    } else if (combined_score >= 3.8) {
        return 4
    } else if (combined_score >= 2.8) {
        return 3
    } else if (combined_score >= 1.8) {
        return 2
    } else {
        return 1
    }
}
const signals = (rsrp, rsrq) => {
    switch (calculateSignalBars(rsrp, rsrq)) {
        case 0:
            return {
                icon: 'mdi-signal-off',
                color: '#dbdbdb'
            }
        case 1:
            return {
                icon: 'mdi-signal-cellular-outline',
                color: '#d70000'
            }
        case 2:
            return {
                icon: 'mdi-signal-cellular-1',
                color: '#ff9e01'
            }
        case 3:
            return {
                icon: 'mdi-signal-cellular-2',
                color: '#849e00'
            }
        case 4:
            return {
                icon: 'mdi-signal-cellular-3',
                color: '#099f00'
            }
        case 5:
            return {
                icon: 'mdi-signal-cellular-3',
                color: '#099f00'
            }
    }
}
const showPayload = async (message) => {
    if (!message?.message_payload ?? null) {
        try {
            const response = await send('get', `devices/message-payload/${message.id}`);
            message.message_payload = response.data?.data?.message_payload ?? null;
            message.raw_hex = response.data?.data?.raw_hex ?? null;
            message.obd_params = response.data?.data?.obd_params ?? null;
        } catch (error) {
            console.log(error);
            showSnackbar('Message payload could not be loaded', 'error');
        }
    }

    if (message.packet_type === 'LOCATION_UPDATE') {
        message.message_payload['payload']['trigger_source'] = message['trigger_source'];
    }

    selectedMessage.value = message;
    dialogSwitch.value = true;
}


// Server-side type-ahead. The picker used to be handed every asset in the
// database inside every logs response, re-sent on each pagination click, and it
// could only offer devices that already had an asset - so unsold stock, which
// still reports during firmware upgrades and wake-ups, was unreachable.
const fetchDevices = (q = '', ids = null) => {
    const seq = ++deviceFetchSeq;
    devicesLoading.value = true;
    const params = {};
    if (q) params.q = q;
    if (ids && ids.length) params.ids = ids;
    send('get', 'devices/message-log-devices', params)
        .then((response) => {
            // Ignore a slow earlier response landing after a newer one.
            const rows = response.data.data ?? [];
            if (ids && ids.length) {
                // Identity lookup, not a search: keep it even if a newer search
                // has started, or the chip renders as a bare id.
                const known = new Map(selectedDevices.value.map((d) => [d.id, d]));
                rows.forEach((d) => known.set(d.id, d));
                selectedDevices.value = [...known.values()];
            }
            if (seq !== deviceFetchSeq) return;
            devices.value = rows;
        })
        .catch(() => {
            if (seq !== deviceFetchSeq) return;
            devices.value = [];
            showSnackbar('Device list could not be loaded', 'error');
        })
        .finally(() => {
            if (seq === deviceFetchSeq) devicesLoading.value = false;
        });
};

const onDeviceSearch = (value) => {
    // Bumped HERE, not inside the debounced call: otherwise a request already
    // in flight still matches the counter and paints results for a query the
    // user has moved on from, and its finally() kills the spinner mid-typing.
    deviceFetchSeq++;
    // Shown immediately, not after the debounce, or the menu reads "no matching
    // devices" while the user is still typing.
    devicesLoading.value = true;
    clearTimeout(deviceSearchTimer);
    deviceSearchTimer = setTimeout(() => fetchDevices(value ?? ''), 250);
};

watch(deviceSearch, (value) => onDeviceSearch(value));

// Keep the chips labelled: remember the full objects for whatever is selected.

onUnmounted(() => clearTimeout(deviceSearchTimer));

const clearAssetFilter = () => {
    formData.asset_id = null;
    fetchMessageLogs(1);
};

const exportLogs = () => {
    // The asset list used to arrive inside every logs response. It is fetched
    // here instead, once, when the dialog that needs it is actually opened.
    // include_disabled so a disabled asset's history stays exportable.
    send('get', 'assets/list', {include_disabled: 1})
        .then((response) => {
            // Serial suffix restored: two vehicles can share a label, and the
            // old embedded list disambiguated them this way.
            assets.value = (response.data.data ?? []).map((a) => ({
                ...a,
                label: a.slno ? `${a.label} (${String(a.slno).trim()})` : a.label,
            }));
        })
        .catch(() => showSnackbar('Assets could not be loaded', 'error'));
    exportLogsDialogSwitch.value = true;
}
const refresh = () => {
    currentPage.value = 1
    fetchMessageLogs()
}
const sort = (sorting) => {
    sortedType.value = sorting.order;
    sortedColumn.value = sorting.column;
    fetchMessageLogs();
}
const formData = reactive({
    asset_id: null,
    device_id: null,
    packet_id: null,
    packet_type: null,
    trigger_source: null,
    from_date: null,
    to_date: null,
});

// Keeps the selected chips labelled when a search refetch replaces the list.
// Declared AFTER formData deliberately: a watcher's getter runs synchronously
// at registration, so registering this above the const would read formData
// inside its temporal dead zone - which threw on mount in dev and left the
// watcher permanently untracked in production.
watch(() => formData.device_id, (ids) => {
    if (!ids || !ids.length) {
        selectedDevices.value = [];
        return;
    }
    const known = new Map([...selectedDevices.value, ...devices.value].map((d) => [d.id, d]));
    selectedDevices.value = ids.map((id) => known.get(id)).filter(Boolean);
});

const fetchMessageLogs = (setPage = null) => {
    loading.value = true;
    currentPage.value = Number.isInteger(setPage) ? setPage : currentPage.value;
    send('get', `devices/message-logs`, {
        page: currentPage.value,
        order: sortedType.value,
        sort: sortedColumn.value,
        ...formData
    }).then((response) => {
        messageLogs.value = response.data.data.data.data;
        totalPages.value = response.data.data.data.meta.last_page;
        currentPage.value = response.data.data.data.meta.current_page;
    }).catch((error) => {
        console.log(error)
        showSnackbar('Message logs could not be loaded', 'error')
    }).finally(() => loading.value = false)
}
</script>

<style scoped>
pre {
    color: #ccc;
    padding: 5px;
    background: #2d2d2d;
    white-space: pre-wrap;
}
</style>
